"use client";

import React, { useState, useCallback, useMemo } from "react";
import { Star } from "lucide-react";
import { cva } from "class-variance-authority";

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

import { cn } from "@/lib/utils";

// Move variants outside component to prevent recreation
const starContainerVariants = cva("flex items-center gap-1", {
  variants: {
    size: {
      sm: "gap-0.5",
      md: "gap-1",
      lg: "gap-1.5",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

const starIconVariants = cva("transition-all duration-200", {
  variants: {
    size: {
      sm: "w-4 h-4",
      md: "w-6 h-6",
      lg: "w-8 h-8",
    },
    variant: {
      default: "",
      yellow: "",
      orange: "",
      red: "",
    },
    state: {
      filled: "",
      empty: "fill-gray-200 text-gray-300",
      hover: "hover:text-gray-400",
    },
    interactive: {
      true: "cursor-pointer hover:scale-110",
      false: "cursor-default",
    },
  },
  compoundVariants: [
    {
      variant: ["primary", "default"],
      state: "filled",
      class: "fill-primary text-primary",
    },
    {
      variant: "orange",
      state: "filled",
      class: "fill-orange-400 text-orange-400",
    },
    {
      variant: "red",
      state: "filled",
      class: "fill-red-400 text-red-400",
    },
  ],
  defaultVariants: {
    size: "md",
    variant: "default",
    state: "empty",
    interactive: true,
  },
});

const labelVariants = cva("transition-all duration-200", {
  variants: {
    interactive: {
      true: "cursor-pointer",
      false: "cursor-default",
    },
  },
  defaultVariants: {
    interactive: true,
  },
});

const ratingTextVariants = cva("text-gray-600", {
  variants: {
    size: {
      sm: "text-xs ml-1.5",
      md: "text-sm ml-2",
      lg: "text-base ml-3",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

// Memoized star component to prevent unnecessary re-renders
const StarItem = React.memo(
  ({ starNumber, isFilled, size, variant, readOnly, onHover, onClick }) => {
    const handleMouseEnter = useCallback(() => {
      onHover(starNumber);
    }, [onHover, starNumber]);

    const handleClick = useCallback(() => {
      onClick(starNumber);
    }, [onClick, starNumber]);

    return (
      <div className="relative">
        <RadioGroupItem
          value={starNumber.toString()}
          id={`star-${starNumber}`}
          className="sr-only"
        />
        <Label
          htmlFor={`star-${starNumber}`}
          className={cn(labelVariants({ interactive: !readOnly }))}
          onMouseEnter={handleMouseEnter}
          onClick={handleClick}
        >
          <Star
            className={cn(
              starIconVariants({
                size,
                variant,
                state: isFilled ? "filled" : "empty",
                interactive: !readOnly,
              }),
              !isFilled && !readOnly && "hover:text-gray-400"
            )}
          />
        </Label>
      </div>
    );
  }
);

StarItem.displayName = "StarItem";

export const StarRating = React.memo(
  ({
    totalStars = 5,
    value = 0,
    onChange,
    readOnly = false,
    size = "md",
    variant = "default",
    className = "",
    withoutLabel = false,
  }) => {
    const [hoveredStar, setHoveredStar] = useState(0);

    // Memoize handlers to prevent child re-renders
    const handleStarClick = useCallback(
      (rating) => {
        if (!readOnly && onChange) {
          onChange(rating);
        }
      },
      [readOnly, onChange]
    );

    const handleStarHover = useCallback(
      (rating) => {
        if (!readOnly) {
          setHoveredStar(rating);
        }
      },
      [readOnly]
    );

    const handleMouseLeave = useCallback(() => {
      if (!readOnly) {
        setHoveredStar(0);
      }
    }, [readOnly]);

    const handleRadioChange = useCallback(
      (val) => {
        onChange?.(parseInt(val));
      },
      [onChange]
    );

    // Memoize star array generation
    const stars = useMemo(() => {
      return Array.from({ length: totalStars }, (_, index) => {
        const starNumber = index + 1;
        const currentRating = hoveredStar || value;
        const isFilled = starNumber <= currentRating;

        return {
          starNumber,
          isFilled,
        };
      });
    }, [totalStars, hoveredStar, value]);

    // Memoize class names
    const containerClassName = useMemo(
      () => cn(starContainerVariants({ size }), className),
      [size, className]
    );

    const ratingTextClassName = useMemo(
      () => cn(ratingTextVariants({ size })),
      [size]
    );

    // Read-only optimized render
    if (readOnly) {
      return (
        <div className={containerClassName}>
          {stars.map(({ starNumber, isFilled }) => (
            <Star
              key={starNumber}
              className={cn(
                starIconVariants({
                  size,
                  variant,
                  state: isFilled ? "filled" : "empty",
                  interactive: false,
                })
              )}
            />
          ))}
          {withoutLabel === false && (
            <span className={ratingTextClassName}>
              {value} out of {totalStars}
            </span>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center">
        <RadioGroup
          value={value.toString()}
          onValueChange={handleRadioChange}
          className={containerClassName}
          onMouseLeave={handleMouseLeave}
        >
          {stars.map(({ starNumber, isFilled }) => (
            <StarItem
              key={starNumber}
              starNumber={starNumber}
              isFilled={isFilled}
              size={size}
              variant={variant}
              readOnly={readOnly}
              onHover={handleStarHover}
              onClick={handleStarClick}
            />
          ))}
        </RadioGroup>

        {withoutLabel === false && (hoveredStar > 0 || value > 0) && (
          <span className={ratingTextClassName}>
            {hoveredStar || value} out of {totalStars} stars
          </span>
        )}
      </div>
    );
  }
);

StarRating.displayName = "StarRating";
